import type { MutableRefObject } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { SIZES } from '../../../../../../muiTheme/MuiDataGrid';
import { useTranslation } from 'react-i18next';
import { trimLeadingZeros } from 'components/editor/helpers/trimLeadingZeros';
import { CURRENCY_REFERENCE, DOLLAR_SIGN, PERCENT_SIGN } from '../defaultData';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useSelector } from 'react-redux';
import PyTextInput from 'components/editor/shared/components/PyMuiComponents/PyTextInput';

type InputWithToolTipsFooterProps = Partial<GridRenderEditCellParams> & {
  tooltipTitle?: string | null;
  onInputChange?: (value: string) => void;
  children?: React.ReactNode;
  rowType: string;
  value: number | string;
  rowId: string;
  inputRefs: MutableRefObject<Record<string, any>>;
  inputType?: 'number' | 'text';
  isHidden?: boolean;
  currencyOrPercentageSymbol?: string;
};

const TotalRowToolTip = styled('span')<{
  isHidden: boolean;
}>(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  fontSize: '1rem',
  paddingRight: 0,
  input: {
    border: 'none',
    paddingTop: 0,
    paddingBottom: 0,
    textAlign: 'right',
    lineHeight: SIZES.defaultRowHeight,
    width: '100%',
    backgroundColor: 'transparent',
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&:focus': {
      outline: 'none',
    },
  },
}));

export const TooltipInputRedux: React.FC<InputWithToolTipsFooterProps> = ({
  currencyOrPercentageSymbol = '',
  rowId,
  inputRefs,
  onInputChange,
  children,
  value,
  isHidden = false,
  inputType,
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);
  const activeCurrency = useSelector((state: RootState) => state?.documentSettings.currencySettings?.symbol) || DOLLAR_SIGN;
  const tooltipTitle = isHidden && t('document.grid.table.cell_hidden_tooltip');
  const isPricingInputWithCurrency = currencyOrPercentageSymbol === CURRENCY_REFERENCE;

  const handleOnChange = async (value: string) => {
    setLocalValue(value);
    onInputChange?.(value);
  };

  const handleOnBlur = async () => {
    const trimmedValue = trimLeadingZeros(localValue);
    setLocalValue(trimmedValue);
    onInputChange?.(trimmedValue);
  };

  useEffect(() => {
    if (value?.toString() !== localValue) {
      setLocalValue(value?.toString() ?? '');
    }
  }, [value]);

  return (
    <Tooltip title={tooltipTitle} placement="bottom">
      <TotalRowToolTip isHidden={isHidden}>
        <PyTextInput
          ref={(el) => (inputRefs.current[rowId] = el)}
          className="py-custom-input"
          type={inputType}
          onChange={(e) => handleOnChange(e.target.value)}
          onBlur={() => handleOnBlur()}
          data-testid="input-with-tooltips"
          value={localValue}
          variant="standard"
          InputProps={{
            sx: {
              fontWeight: '600',
              paddingLeft: 0,
              '& .MuiTypography-root': {
                fontWeight: '600',
              },
            },
            endAdornment: !isPricingInputWithCurrency ? (
              <InputAdornment position="end" component="div">
                {PERCENT_SIGN}
              </InputAdornment>
            ) : (
              ''
            ),
            startAdornment: isPricingInputWithCurrency ? (
              <InputAdornment position="start" component="div">
                {activeCurrency}
              </InputAdornment>
            ) : (
              ''
            ),
            disableUnderline: true,
          }}
        />

        {children}
      </TotalRowToolTip>
    </Tooltip>
  );
};

export default TooltipInputRedux;
